import React from 'react';
import { calculateMonthlyCashflow, calculateKaufnebenkosten } from './utils/dealCalc';
import { fmt } from './utils/fmt';
import { useRules } from '../../hooks/useRules';

function getCellStyle(cf, tokens) {
  if (cf > 200)  return { bg: tokens['bg-highlight-pos'] ?? '#DCFCE7', color: tokens['value-green']        ?? '#1FA534' };
  if (cf > 0)    return { bg: tokens['bg-positive']      ?? '#F0FDF4', color: tokens['value-green']        ?? '#16A34A' };
  if (cf > -200) return { bg: tokens['signal-medium-bg'] ?? '#FFFBEB', color: tokens['signal-medium-text'] ?? '#9A6B14' };
  return               { bg: tokens['bg-critical']       ?? '#FEF2F2', color: tokens['value-red']          ?? '#E83838' };
}

export default function SensitivityHeatmap({ baseline, params }) {
  const rules = useRules();
  const tokens = rules?.color_tokens ?? {};

  if (!baseline?.monatskaltmiete || !params) return null;

  const simKaufpreis = Math.round(baseline.kaufpreis * (1 + params.kaufpreisAnpassung / 100));
  const simMiete = Math.round(baseline.monatskaltmiete * (1 + params.mieteAnpassung / 100));
  const ekPct = params.eigenkapital / 100;
  const nk = calculateKaufnebenkosten(simKaufpreis, baseline.bundesland, baseline.maklerAnteil);
  const gesamt = simKaufpreis + nk.gesamt;

  const currentZins = params.zinssatz / 100;
  const currentTilg = params.tilgung / 100;

  const zinsen    = [0.020, 0.025, 0.030, 0.035, 0.040, 0.045];
  const tilgungen = [0.010, 0.015, 0.020, 0.025, 0.030];

  const matrix = zinsen.map(z => tilgungen.map(t => {
    const dk = gesamt - Math.round(gesamt * ekPct);
    return calculateMonthlyCashflow({
      monatskaltmiete: simMiete, darlehensbetrag: dk,
      zinssatz: z, tilgungssatz: t,
      wohnflaeche: baseline.wohnflaeche, baujahr: baseline.baujahr,
    }).cashflow;
  }));

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E0E0DF',
      borderRadius: 16,
      padding: 16,
    }}>
      {/* Section Label */}
      <div style={{
        fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.6px', color: '#8C8FA0', marginBottom: 4,
      }}>
        Sensitivity — Cashflow bei Zins × Tilgung
      </div>
      <p style={{ fontSize: 12, color: '#8C8FA0', marginBottom: 16, marginTop: 0 }}>
        Cashflow €/Mo. bei verschiedenen Zins/Tilgung-Kombinationen
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{
                textAlign: 'left', fontSize: 11, fontWeight: 600,
                color: '#8C8FA0', textTransform: 'uppercase',
                letterSpacing: '0.04em', paddingBottom: 8, paddingRight: 8, width: 90,
              }}>
                Zins ↓ / Tilg. →
              </th>
              {tilgungen.map(t => (
                <th key={t} style={{
                  textAlign: 'center', fontSize: 11, fontWeight: 600,
                  textTransform: 'uppercase', paddingBottom: 8, paddingLeft: 4, paddingRight: 4,
                  color: Math.abs(t - currentTilg) < 0.001 ? '#1DB3B3' : '#8C8FA0',
                }}>
                  {(t * 100).toFixed(1)}%
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zinsen.map((z, zi) => (
              <tr key={z}>
                <td style={{
                  fontSize: 12, fontWeight: 600,
                  paddingTop: 4, paddingBottom: 4, paddingRight: 8,
                  fontVariantNumeric: 'tabular-nums',
                  color: Math.abs(z - currentZins) < 0.001 ? '#1DB3B3' : '#8C8FA0',
                }}>
                  {(z * 100).toFixed(1)}%
                </td>
                {matrix[zi].map((cf, ti) => {
                  const isCurrent =
                    Math.abs(zinsen[zi] - currentZins) < 0.002 &&
                    Math.abs(tilgungen[ti] - currentTilg) < 0.002;
                  const s = getCellStyle(cf, tokens);
                  return (
                    <td key={ti} style={{ textAlign: 'center', padding: '4px' }}>
                      <div style={{
                        borderRadius: 6,
                        padding: '6px 4px',
                        fontSize: 12, fontWeight: 600,
                        background: s.bg, color: s.color,
                        fontVariantNumeric: 'tabular-nums',
                        transition: 'all 0.2s ease',
                        ...(isCurrent ? {
                          outline: '2px solid #1DB3B3',
                          outlineOffset: '1px',
                          boxShadow: '0 0 0 4px rgba(29,179,179,0.15)',
                        } : {}),
                      }}>
                        {cf >= 0 ? '+' : ''}{fmt(cf)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 10, color: '#8C8FA0', marginTop: 12, marginBottom: 0 }}>
        Grün = positiver Cashflow · Rot = negativer Cashflow · Hervorgehoben = aktuelle Auswahl
      </p>
    </div>
  );
}
